import NotFoundPage from "pages/not-found";
import {
  ProjectContext,
  TProjectContext,
  isPermissionNotGrantted
} from "context/ProjectProvider";
import { useContext, useEffect, useMemo } from "react";
import { Spin, Tabs } from "antd";
import { useHistory, useLocation, useParams } from "react-router";
import { ErrorMessages, ProjectPermissionEnum } from "../../constants";
import ProjectReportsTab from "./project-reports/project-reports-tab";
import AuditReportsTab from "./audit-reports/audit-reports-tab";

const REPORTS_TAB_PROJECT = "project-reports";
const REPORTS_TAB_AUDIT = "audit-reports";

function ReportsPageManu() {
  const history = useHistory();
  const { pathname } = useLocation();
  const { projectId } = useParams() as { projectId: string };
  const {
    tokenContents,
    isProductEntitlementsDataAvailable,
    isFeatureFlagEnabled
  }: TProjectContext = useContext(ProjectContext);

  const isActivityLogEnabled = isFeatureFlagEnabled("ACTIVITY_LOG");

  const activeTab = useMemo(() => {
    if (isActivityLogEnabled && pathname.endsWith(`/${REPORTS_TAB_AUDIT}`)) {
      return REPORTS_TAB_AUDIT;
    }
    return REPORTS_TAB_PROJECT;
  }, [pathname, isActivityLogEnabled]);

  useEffect(() => {
    if (
      isProductEntitlementsDataAvailable &&
      !isActivityLogEnabled &&
      pathname.endsWith(`/${REPORTS_TAB_AUDIT}`)
    ) {
      history.replace(`/project/${projectId}/reports/${REPORTS_TAB_PROJECT}`);
    }
  }, [
    history,
    isActivityLogEnabled,
    isProductEntitlementsDataAvailable,
    pathname,
    projectId
  ]);

  const cannotViewProjectReports = tokenContents
    ? isPermissionNotGrantted(
        ProjectPermissionEnum.ViewProjectReports,
        tokenContents?.role
      )
    : false;

  if (cannotViewProjectReports) {
    return (
      <NotFoundPage
        status="403"
        title="403"
        subTitle={ErrorMessages.UnauthorizedPageAccess}
      />
    );
  }

  const loadingUI = (
    <div className="w-full h-full bg-ciq-gray-50 flex justify-center items-center">
      <Spin size="large" />
    </div>
  );

  const tabItems = [
    {
      key: REPORTS_TAB_PROJECT,
      label: (
        <span data-testid="reports-tab-project-reports">Project Reports</span>
      ),
      children: <ProjectReportsTab />
    }
  ];

  if (isActivityLogEnabled) {
    tabItems.push({
      key: REPORTS_TAB_AUDIT,
      label: <span data-testid="reports-tab-audit-reports">Audit Reports</span>,
      children: <AuditReportsTab />
    });
  }

  return (
    <div className="flex flex-col h-full w-full" data-testid="reports-page">
      <div
        className="w-full px-8 py-4 font-semibold text-xl"
        data-testid="reports-page-title"
      >
        Reports
      </div>
      {isProductEntitlementsDataAvailable && (
        <div className="h-full overflow-auto bg-ciq-gray-50">
          <Tabs
            className="reports-menu-tabs px-6"
            activeKey={activeTab}
            onChange={(key) => {
              history.push(`/project/${projectId}/reports/${key}`);
            }}
            items={tabItems}
            data-testid="reports-menu-tabs"
          />
        </div>
      )}
      {!isProductEntitlementsDataAvailable && loadingUI}
    </div>
  );
}

export default ReportsPageManu;
